/** Sora api-server client for mobile (`https://sora.netflixsubs.app/v1`). */

export const SORA_BASE_URL = 'https://sora.netflixsubs.app';

/** Shared secret (same threat model as extension — public after ship). */
export const SORA_API_KEY = 'CIFJG7m22zKsBNSnlO56oN70aSmlxTuI';

export type SoraAuthOpts = {
  isMember?: boolean;
  userId?: string;
  /** Forwarded for future server-side gates; ignored by api-server today. */
  userToken?: string;
};

export type SoraAsrSource = 'asr' | 'creator';

export type SoraCreateJobBody =
  | {
      type: 'enhance';
      videoId: string;
      tier?: 'full' | 'trial';
      asrSource?: SoraAsrSource;
      /** Creator enhance language bucket (must match asr-cache `lc`). */
      lc?: string;
    }
  | { type: 'whisper'; videoId: string }
  | {
      type: 'translate';
      source: 'enhance' | 'whisper' | 'netflix';
      videoId: string;
      targetLang: string;
    };

export type SoraJobPoll = {
  status: 'pending' | 'queued' | 'active' | 'done' | 'failed' | 'cancelled';
  jobId?: string;
  result?: unknown;
  position?: number;
  progress?: number | null;
  phase?: 'download' | 'transcribe';
  error?: string;
};

export type SoraClientOptions = {
  baseUrl?: string;
  apiKey?: string;
  fetchImpl?: typeof fetch;
  getAuth?: () => SoraAuthOpts;
};

function soraHeaders(auth: SoraAuthOpts = {}, apiKey = SORA_API_KEY): Record<string, string> {
  const headers: Record<string, string> = {
    Accept: 'application/json',
    'X-Sora-Key': apiKey,
  };
  if (auth.isMember) headers['X-Sora-Member'] = '1';
  if (auth.userId) headers['X-Sora-User'] = auth.userId;
  if (auth.userToken) headers['X-Sora-Token'] = auth.userToken;
  return headers;
}

/** Exported for unit tests — same headers createJob / getJob / uploads use. */
export function buildSoraHeadersForTests(
  auth: SoraAuthOpts = {},
  apiKey = SORA_API_KEY,
): Record<string, string> {
  return soraHeaders(auth, apiKey);
}

export type SoraCaptionTrack = {
  baseUrl?: string;
  name?: { simpleText?: string };
  vssId?: string;
  languageCode?: string;
  kind?: string;
  enhanced?: boolean;
  /** Creator→ASR map then enhance; must not set `enhanced`. */
  creatorEnhanced?: boolean;
  whisper?: boolean;
  restored?: boolean;
  downloadable?: boolean;
  /** Paired ASR timedtext URL when `creatorEnhanced` is set. */
  asrBaseUrl?: string;
};

export type SoraMetadataPayload = {
  metadata: {
    videoId: string;
    captionTracks: SoraCaptionTrack[];
  };
  selections: {
    subtitles: Array<{
      name: string;
      value: number;
      code?: string;
      downloadable?: boolean;
    }>;
    translations: Array<{ name: string; value: number; code: string }>;
  };
};

export async function fetchYouTubeMetadata(
  videoId: string,
  opts: SoraClientOptions = {},
): Promise<SoraMetadataPayload> {
  const base = (opts.baseUrl ?? SORA_BASE_URL).replace(/\/$/, '');
  const key = opts.apiKey ?? SORA_API_KEY;
  const fetchImpl = opts.fetchImpl ?? fetch.bind(globalThis);
  const res = await fetchImpl(
    `${base}/v1/youtube/${encodeURIComponent(videoId)}/metadata`,
    { headers: soraHeaders({}, key) },
  );
  if (!res.ok) {
    throw new Error(`Sora metadata failed: ${res.status}`);
  }
  return (await res.json()) as SoraMetadataPayload;
}

export async function fetchYouTubeCaptions(
  videoId: string,
  trackIndex: number,
  opts: SoraClientOptions = {},
): Promise<{ json3: unknown; kind: 'asr' | 'normal' }> {
  const base = (opts.baseUrl ?? SORA_BASE_URL).replace(/\/$/, '');
  const key = opts.apiKey ?? SORA_API_KEY;
  const fetchImpl = opts.fetchImpl ?? fetch.bind(globalThis);
  const url = new URL(
    `${base}/v1/youtube/${encodeURIComponent(videoId)}/captions`,
  );
  url.searchParams.set('trackIndex', String(trackIndex));
  const res = await fetchImpl(url.toString(), { headers: soraHeaders({}, key) });
  if (!res.ok) {
    let detail = String(res.status);
    try {
      const body = (await res.json()) as { error?: string };
      if (body.error) detail = body.error;
    } catch {
      /* ignore */
    }
    throw new Error(`Sora captions failed: ${detail}`);
  }
  const headerKind = res.headers.get('X-Caption-Kind');
  const kind = headerKind === 'asr' ? 'asr' : 'normal';
  const json3 = (await res.json()) as unknown;
  return { json3, kind };
}

export async function createJob(
  body: SoraCreateJobBody,
  auth: SoraAuthOpts = {},
  fetchImpl: typeof fetch = fetch,
  baseUrl = SORA_BASE_URL,
  apiKey = SORA_API_KEY,
): Promise<{ jobId?: string; status?: string; result?: unknown; error?: string; httpStatus: number }> {
  const response = await fetchImpl(`${baseUrl.replace(/\/$/, '')}/v1/jobs`, {
    method: 'POST',
    headers: { ...soraHeaders(auth, apiKey), 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  const json = (await response.json().catch(() => ({}))) as Record<string, unknown>;
  return {
    httpStatus: response.status,
    jobId: typeof json.jobId === 'string' ? json.jobId : undefined,
    status: typeof json.status === 'string' ? json.status : undefined,
    result: json.result,
    error: typeof json.error === 'string' ? json.error : undefined,
  };
}

export async function getJob(
  jobId: string,
  auth: SoraAuthOpts = {},
  fetchImpl: typeof fetch = fetch,
  baseUrl = SORA_BASE_URL,
  apiKey = SORA_API_KEY,
): Promise<SoraJobPoll | undefined> {
  const response = await fetchImpl(
    `${baseUrl.replace(/\/$/, '')}/v1/jobs/${encodeURIComponent(jobId)}`,
    {
      method: 'GET',
      headers: soraHeaders(auth, apiKey),
    },
  );
  if (!response.ok && response.status !== 404) {
    return undefined;
  }
  return (await response.json()) as SoraJobPoll;
}

export function shouldClearCachedSoraJobId(
  poll: Pick<SoraJobPoll, 'status'> | { status?: string; error?: string } | undefined,
): boolean {
  if (!poll) return false;
  if ('error' in poll && poll.error === 'job_not_found') return true;
  if (!poll.status) return true;
  return poll.status === 'done' || poll.status === 'failed' || poll.status === 'cancelled';
}

export function mapSoraCreateFailureToLegacy(created: {
  httpStatus: number;
  error?: string;
}): { response: 'Dismiss'; desc: 'source' } | undefined {
  if (created.httpStatus === 409 || created.error === 'source_not_ready') {
    return { response: 'Dismiss', desc: 'source' };
  }
  return undefined;
}

export async function uploadAsrCache(
  videoId: string,
  payload: unknown,
  languageCode: string,
  auth: SoraAuthOpts = {},
  fetchImpl: typeof fetch = fetch,
  baseUrl = SORA_BASE_URL,
  apiKey = SORA_API_KEY,
  asrSource?: SoraAsrSource,
): Promise<void> {
  let body: Record<string, unknown>;
  if (asrSource === 'creator') {
    const pair = payload as { creator?: unknown; asr?: unknown; events?: unknown };
    if (pair.creator != null && pair.asr != null) {
      body = {
        creator: pair.creator,
        asr: pair.asr,
        lc: languageCode,
        asrSource: 'creator',
      };
    } else {
      // Creator-only (server synth) or legacy timed events.
      body = {
        ...(payload as object),
        lc: languageCode,
        asrSource: 'creator',
      };
    }
  } else {
    body = {
      ...(payload as object),
      lc: languageCode,
    };
  }
  const response = await fetchImpl(
    `${baseUrl.replace(/\/$/, '')}/v1/youtube/${encodeURIComponent(videoId)}/asr-cache`,
    {
      method: 'POST',
      headers: { ...soraHeaders(auth, apiKey), 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    },
  );
  if (!response.ok) {
    throw new Error(`asr-cache upload failed: ${response.status}`);
  }
}

/** Upload cue list as translate source cache (extension Batch path; id = `${vid}-${trackId}`). */
export async function uploadNetflixSubtitles(
  id: string,
  cues: unknown[],
  auth: SoraAuthOpts = {},
  fetchImpl: typeof fetch = fetch,
  baseUrl = SORA_BASE_URL,
  apiKey = SORA_API_KEY,
): Promise<{ ok?: boolean }> {
  const response = await fetchImpl(
    `${baseUrl.replace(/\/$/, '')}/v1/netflix/${encodeURIComponent(id)}/subtitles`,
    {
      method: 'POST',
      headers: { ...soraHeaders(auth, apiKey), 'Content-Type': 'application/json' },
      body: JSON.stringify({ cues }),
    },
  );
  return (await response.json().catch(() => ({}))) as { ok?: boolean };
}

export function mapSoraJobToLegacy(poll: SoraJobPoll): {
  response?: string;
  result?: unknown;
  position?: number;
  progress?: number;
  desc?: string;
  phase?: 'download' | 'transcribe';
} {
  if (poll.status === 'done' && poll.result != null) {
    return { response: 'OK', result: poll.result };
  }
  if (poll.status === 'queued' || poll.status === 'active' || poll.status === 'pending') {
    const position =
      poll.status === 'queued' && typeof poll.position === 'number'
        ? Math.max(1, poll.position)
        : 1;
    const progress = typeof poll.progress === 'number' ? poll.progress : undefined;
    return {
      response: 'Queuing',
      position,
      progress,
      ...(poll.phase === 'download' || poll.phase === 'transcribe'
        ? { phase: poll.phase }
        : {}),
    };
  }
  if (poll.status === 'cancelled') {
    return { response: 'Dismiss', desc: 'cancelled' };
  }
  if (poll.status === 'failed') {
    switch (poll.error) {
      case 'asr_not_found':
        return { response: 'Dismiss', desc: 'info' };
      case 'is_live':
        return { response: 'Dismiss', desc: 'live' };
      case 'too_long':
        return { response: 'Dismiss', desc: 'length' };
      case 'source_not_ready':
        return { response: 'Dismiss', desc: 'source' };
      case 'enhance_timeout':
        return { response: 'Dismiss', desc: 'enhance_timeout' };
      case 'enhance_failed':
        return { response: 'Dismiss', desc: 'enhance' };
      case 'upstream_error':
        return { response: 'Dismiss', desc: 'upstream' };
      default:
        return { response: 'Failed' };
    }
  }
  return { response: 'Failed' };
}
